import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Smartphone, Download, Star, Users } from 'lucide-react';

export const AppDownloadSection = () => {
  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.webcare.paying_guest&pcampaignid=web_share";
  
  return (
    <section className="py-20 bg-gradient-to-br from-[#6366F1]/10 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#6366F1]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Smartphone className="h-4 w-4" />
            <span>Mobile App</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Download Our
            <span className="block text-[#6366F1]">Mobile App</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Manage your PG on-the-go with our mobile application. 
            Available for Android devices on Google Play Store.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900">
                PG Management at Your Fingertips
              </h3>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Access all your PG management features directly from your mobile device. 
                Perfect for managing operations while you're away from your desk.
              </p>
            </div>

            {/* App Features */}
            <div className="space-y-4">
              {[
                'Real-time rent collection notifications',
                'Instant tenant communication via WhatsApp',
                'Mobile-friendly maintenance tracking',
                'Quick access to tenant information',
                'Push notifications for important updates'
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-[#6366F1]/10 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-[#6366F1] rounded-full"></div>
                  </div>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* App Stats */}
            <div className="grid grid-cols-2 gap-6 py-6 border-t border-gray-200">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <span className="text-2xl font-bold text-gray-900">4.5</span>
                </div>
                <div className="text-sm text-gray-600">Play Store Rating</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Users className="h-5 w-5 text-[#6366F1]" />
                  <span className="text-2xl font-bold text-gray-900">1K+</span>
                </div>
                <div className="text-sm text-gray-600">Active Users</div>
              </div>
            </div>

            {/* Download Button */}
            <div className="pt-4">
              <a 
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  className="bg-[#6366F1] hover:bg-[#5856eb] text-white px-8 py-4 text-lg font-semibold group"
                >
                  <Download className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                  Download from Play Store
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - QR Code */}
          <div className="flex justify-center lg:justify-end">
            <Card className="bg-white shadow-2xl border border-gray-100 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="w-16 h-16 bg-[#6366F1]/10 rounded-2xl flex items-center justify-center mx-auto">
                      <Smartphone className="h-8 w-8 text-[#6366F1]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Scan to Download
                    </h3>
                    <p className="text-gray-600">
                      Scan the QR code with your Android device to download the app instantly
                    </p>
                  </div>

                  {/* QR Code Placeholder - We'll use a QR service */}
                  <div className="relative">
                    <div className="w-48 h-48 mx-auto bg-white rounded-xl border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(playStoreUrl)}`}
                        alt="Download RentMatez App QR Code"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Corner decorations */}
                    <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-[#6366F1] rounded-tl-lg"></div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-[#6366F1] rounded-tr-lg"></div>
                    <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-[#6366F1] rounded-bl-lg"></div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-[#6366F1] rounded-br-lg"></div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Available Now</span>
                    </div>
                    
                    <p className="text-sm text-gray-500">
                      Compatible with Android 6.0+
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Start Managing Your PG Today
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of PG owners who are already using RentMatez to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1] hover:text-white px-8"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download Mobile App
                </Button>
              </a>
              
              <Button 
                size="lg" 
                className="bg-[#6366F1] hover:bg-[#5856eb] text-white px-8"
              >
                Start Web Trial
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};